import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import safeStorage from '../utils/safeStorage';
import { STORAGE_KEYS } from '../utils/storageKeys';

const ReviewContext = createContext();

export const ReviewProvider = ({ children }) => {
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = () => {
    try {
      const savedReviews = safeStorage.getJSON(STORAGE_KEYS.REVIEWS, []);
      if (Array.isArray(savedReviews)) {
        setReviews(savedReviews);
      }
    } catch (error) {
      console.error('Failed to load reviews:', error);
      setReviews([]);
    }
  };

  const saveReviews = (updatedReviews) => {
    if (safeStorage.setJSON(STORAGE_KEYS.REVIEWS, updatedReviews)) {
      setReviews(updatedReviews);
    }
  };

  // Check if user has purchased a product
  const hasUserPurchasedProduct = (productId) => {
    if (!user) return false;
    
    try {
      const allOrders = safeStorage.getJSON(STORAGE_KEYS.ORDERS, []);
      const userOrders = allOrders.filter(order => order.email === user.email);
      
      return userOrders.some(order => 
        order.items.some(item => {
          // Check if the item matches the product ID
          // Handle both exact matches and partial matches (for variant products)
          return item.id === productId || 
                 item.productId === productId ||
                 (item.id && item.id.includes(productId)) ||
                 (item.productId && item.productId.includes(productId));
        })
      );
    } catch (error) {
      console.error('Error checking purchase history:', error);
      return false;
    }
  };

  // Get user's purchased products that haven't been reviewed
  const getUnreviewedPurchasedProducts = () => {
    if (!user) return [];
    
    try {
      const allOrders = safeStorage.getJSON(STORAGE_KEYS.ORDERS, []);
      const userOrders = allOrders.filter(order => order.email === user.email);
      const purchasedProducts = [];
      
      userOrders.forEach(order => {
        order.items.forEach(item => {
          const productId = item.id || item.productId;
          if (productId) {
            // Check if user has already reviewed this product
            const hasReviewed = reviews.some(review => 
              review.productId === productId && review.userId === user.id
            );
            
            if (!hasReviewed && !purchasedProducts.find(p => p.id === productId)) {
              purchasedProducts.push({
                id: productId,
                name: item.title || item.name,
                image: item.image,
                variant: item.variant,
                orderId: order.id,
                orderDate: order.date
              });
            }
          }
        });
      });
      
      return purchasedProducts.sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));
    } catch (error) {
      console.error('Error getting unreviewed products:', error);
      return [];
    }
  };

  const addReview = async (reviewData) => {
    if (!user) {
      throw new Error('Must be logged in to leave a review');
    }

    // Check if user has purchased this product
    const hasPurchased = hasUserPurchasedProduct(reviewData.productId);
    if (!hasPurchased) {
      throw new Error('You can only review products you have purchased');
    }

    setIsLoading(true);
    try {
      const existingReview = reviews.find(
        review => review.productId === reviewData.productId && review.userId === user.id
      );

      if (existingReview) {
        throw new Error('You have already reviewed this product');
      }

      const newReview = {
        id: `review_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        productId: reviewData.productId,
        productName: reviewData.productName,
        userId: user.id,
        userName: `${user.firstName} ${user.lastName}`,
        userEmail: user.email,
        rating: reviewData.rating,
        title: reviewData.title,
        comment: reviewData.comment,
        verified: hasPurchased, // Only mark as verified if actually purchased
        helpful: 0,
        reported: false,
        status: 'approved',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const updatedReviews = [...reviews, newReview];
      saveReviews(updatedReviews);

      console.log('✅ Review added successfully:', newReview.id);
      return newReview;
    } catch (error) {
      console.error('Failed to add review:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getProductReviews = (productId, friendUserIds = []) => {
    const friendSet = new Set(Array.isArray(friendUserIds) ? friendUserIds : []);
    return reviews
      .filter(review => review.productId === productId && review.status === 'approved')
      .sort((a, b) => {
        // Friends first: reviews from friends appear before others
        const aIsFriend = friendSet.size > 0 ? friendSet.has(a.userId) : false;
        const bIsFriend = friendSet.size > 0 ? friendSet.has(b.userId) : false;
        if (aIsFriend && !bIsFriend) return -1;
        if (!aIsFriend && bIsFriend) return 1;

        // Then by reaction count (descending), then by date (descending)
        const getTotalReactions = (review) => {
          if (!review.reactions) return 0;
          return Object.values(review.reactions).reduce((total, userReactions) => {
            return total + userReactions.length;
          }, 0);
        };

        const aReactions = getTotalReactions(a);
        const bReactions = getTotalReactions(b);

        if (aReactions !== bReactions) {
          return bReactions - aReactions;
        }
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
  };

  const getProductRating = (productId) => {
    const productReviews = getProductReviews(productId);
    if (productReviews.length === 0) {
      return { average: 0, count: 0, distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } };
    }

    const ratings = productReviews.map(review => review.rating);
    const average = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
    
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    ratings.forEach(rating => {
      distribution[rating]++;
    });

    return {
      average: Math.round(average * 10) / 10,
      count: productReviews.length,
      distribution
    };
  };

  const addEmojiReaction = (reviewId, emoji) => {
    if (!user) {
      throw new Error('Must be logged in to react to reviews');
    }

    try {
      const updatedReviews = reviews.map(review => {
        if (review.id === reviewId) {
          const reactions = review.reactions || {};
          const userReactions = reactions[user.id] || [];
          
          // Toggle emoji reaction (remove if already exists, add if not)
          const emojiIndex = userReactions.indexOf(emoji);
          if (emojiIndex > -1) {
            userReactions.splice(emojiIndex, 1);
          } else {
            userReactions.push(emoji);
          }
          
          // Update reactions object
          reactions[user.id] = userReactions;
          
          return {
            ...review,
            reactions,
            updatedAt: new Date().toISOString()
          };
        }
        return review;
      });

      saveReviews(updatedReviews);
      console.log('✅ Emoji reaction updated for review:', reviewId);
    } catch (error) {
      console.error('Failed to add emoji reaction:', error);
      throw error;
    }
  };

  const getReviewReactions = (reviewId) => {
    const review = reviews.find(r => r.id === reviewId);
    if (!review || !review.reactions) return {};

    const allReactions = {};
    Object.values(review.reactions).forEach(userReactions => {
      userReactions.forEach(emoji => {
        allReactions[emoji] = (allReactions[emoji] || 0) + 1;
      });
    });

    return allReactions;
  };

  const getUserReactions = (reviewId) => {
    if (!user) return [];
    const review = reviews.find(r => r.id === reviewId);
    return review?.reactions?.[user.id] || [];
  };

  const hasUserReviewed = (productId) => {
    return reviews.some(review => 
      review.productId === productId && 
      review.userId === user?.id && 
      review.status === 'approved'
    );
  };

  const deleteReview = async (reviewId) => {
    if (!user) {
      throw new Error('Must be logged in to delete reviews');
    }

    try {
      const review = reviews.find(r => r.id === reviewId);
      if (!review) {
        throw new Error('Review not found');
      }

      if (review.userId !== user.id) {
        throw new Error('You can only delete your own reviews');
      }

      const updatedReviews = reviews.filter(r => r.id !== reviewId);
      saveReviews(updatedReviews);
      console.log('✅ Review deleted successfully:', reviewId);
    } catch (error) {
      console.error('Failed to delete review:', error);
      throw error;
    }
  };

  const markHelpful = async (reviewId) => {
    if (!user) {
      throw new Error('Must be logged in to mark reviews as helpful');
    }

    try {
      const updatedReviews = reviews.map(review => {
        if (review.id === reviewId && review.userId !== user.id) {
          const helpfulUsers = review.helpfulUsers || [];
          const userIndex = helpfulUsers.indexOf(user.id);
          
          if (userIndex > -1) {
            // Remove from helpful
            helpfulUsers.splice(userIndex, 1);
          } else {
            // Add to helpful
            helpfulUsers.push(user.id);
          }
          
          return {
            ...review,
            helpful: helpfulUsers.length,
            helpfulUsers,
            updatedAt: new Date().toISOString()
          };
        }
        return review;
      });

      saveReviews(updatedReviews);
      console.log('✅ Helpful status updated for review:', reviewId);
    } catch (error) {
      console.error('Failed to mark helpful:', error);
      throw error;
    }
  };

  // Get users who liked a review
  const getReviewLikes = (reviewId) => {
    const review = reviews.find(r => r.id === reviewId);
    if (!review || !review.helpfulUsers) return [];
    
    try {
      const allUsers = safeStorage.getJSON(STORAGE_KEYS.USERS, []);
      return review.helpfulUsers.map(userId => {
        const user = allUsers.find(u => u.id === userId);
        return user ? {
          id: user.id,
          name: `${user.firstName} ${user.lastName}`,
          email: user.email
        } : {
          id: userId,
          name: 'Unknown User',
          email: 'unknown@example.com'
        };
      });
    } catch (error) {
      console.error('Error getting review likes:', error);
      return [];
    }
  };

  // Check if current user has liked a review
  const hasUserLikedReview = (reviewId) => {
    if (!user) return false;
    const review = reviews.find(r => r.id === reviewId);
    return review?.helpfulUsers?.includes(user.id) || false;
  };

  const reportReview = async (reviewId, reason) => {
    if (!user) {
      throw new Error('Must be logged in to report reviews');
    }

    try {
      const updatedReviews = reviews.map(review => {
        if (review.id === reviewId && review.userId !== user.id) {
          const reports = review.reports || [];
          const existingReport = reports.find(r => r.userId === user.id);
          
          if (!existingReport) {
            reports.push({
              userId: user.id,
              reason,
              createdAt: new Date().toISOString()
            });
          }
          
          return {
            ...review,
            reports,
            reported: reports.length >= 3, // Auto-flag if 3+ reports
            updatedAt: new Date().toISOString()
          };
        }
        return review;
      });

      saveReviews(updatedReviews);
      console.log('✅ Review reported:', reviewId);
    } catch (error) {
      console.error('Failed to report review:', error);
      throw error;
    }
  };

  const value = {
    reviews,
    isLoading,
    addReview,
    getProductReviews,
    getProductRating,
    loadReviews,
    addEmojiReaction,
    getReviewReactions,
    getUserReactions,
    hasUserReviewed,
    deleteReview,
    markHelpful,
    reportReview,
    hasUserPurchasedProduct,
    getUnreviewedPurchasedProducts,
    getReviewLikes,
    hasUserLikedReview
  };

  return (
    <ReviewContext.Provider value={value}>
      {children}
    </ReviewContext.Provider>
  );
};

export const useReviews = () => {
  const context = useContext(ReviewContext);
  if (!context) {
    console.warn('useReviews used outside ReviewProvider - returning default values');
    return {
      reviews: [],
      isLoading: false,
      addReview: async () => { throw new Error('Reviews not available'); },
      getProductReviews: () => [],
      getProductRating: () => ({ average: 0, count: 0, distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } }),
      loadReviews: () => {},
      addEmojiReaction: async () => { throw new Error('Reviews not available'); },
      getReviewReactions: () => ({}),
      getUserReactions: () => ([]),
      hasUserReviewed: () => false,
      deleteReview: async () => { throw new Error('Reviews not available'); },
      markHelpful: async () => { throw new Error('Reviews not available'); },
      reportReview: async () => { throw new Error('Reviews not available'); },
      hasUserPurchasedProduct: () => false,
      getUnreviewedPurchasedProducts: () => [],
      getReviewLikes: () => [],
      hasUserLikedReview: () => false
    };
  }
  return context;
};

export default ReviewContext;
